import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { useAiConsent } from '@/context/AiConsentContext';
import {
  AI_CONSENT_ACCEPT_LABEL,
  AI_CONSENT_DECLINE,
  AI_CONSENT_DECLINE_LABEL,
  AI_CONSENT_FLOWS,
  AI_CONSENT_INTRO,
  AI_CONSENT_RECIPIENT,
  AI_CONSENT_TITLE,
} from '@/lib/ai-consent-copy';

type AiConsentSheetProps = {
  open: boolean;
  onDecide: (status: 'granted' | 'declined') => void;
  busy?: boolean;
};

const PRIVACY_URL = 'https://jilu.io/privacy';

/**
 * The sheet is blocking, so an in-app route change would leave the policy
 * stranded behind an overlay the user cannot dismiss. Open it above the sheet
 * instead: the system browser on native, a new tab on web.
 */
const openPrivacyPolicy = async () => {
  if (Capacitor.isNativePlatform()) {
    await Browser.open({ url: PRIVACY_URL });
    return;
  }
  window.open(PRIVACY_URL, '_blank', 'noopener,noreferrer');
};

/**
 * Blocking by design: no close button, and outside/escape dismissal is
 * prevented. "Not now" is an unpenalized choice, which is what keeps a forced
 * decision from being a dark pattern.
 */
export const AiConsentSheet: React.FC<AiConsentSheetProps> = ({ open, onDecide, busy }) => (
  <Sheet open={open}>
    <SheetContent
      side="bottom"
      hideClose
      onPointerDownOutside={(e) => e.preventDefault()}
      onEscapeKeyDown={(e) => e.preventDefault()}
      onInteractOutside={(e) => e.preventDefault()}
      className="rounded-t-2xl max-h-[92vh] overflow-y-auto text-left dark:border-primary/20"
    >
      <SheetHeader className="text-left">
        <SheetTitle>{AI_CONSENT_TITLE}</SheetTitle>
      </SheetHeader>

      <div className="space-y-4 text-sm">
        <p>{AI_CONSENT_INTRO}</p>

        <div className="space-y-2">
          <p className="font-semibold">What's sent</p>
          <ul className="list-disc space-y-1 pl-5">
            {AI_CONSENT_FLOWS.map((flow) => (
              <li key={flow.label}>
                <span className="font-medium">{flow.label}</span> — {flow.detail}
              </li>
            ))}
          </ul>
        </div>

        <div className="space-y-2">
          <p className="font-semibold">Who receives it</p>
          <p>{AI_CONSENT_RECIPIENT}</p>
        </div>

        <div className="space-y-2">
          <p className="font-semibold">If you choose not to</p>
          <p>{AI_CONSENT_DECLINE}</p>
        </div>
      </div>

      <div className="flex flex-col gap-2 pt-2">
        <Button onClick={() => onDecide('granted')} disabled={busy}>
          {AI_CONSENT_ACCEPT_LABEL}
        </Button>
        <Button variant="outline" onClick={() => onDecide('declined')} disabled={busy}>
          {AI_CONSENT_DECLINE_LABEL}
        </Button>
        <button
          type="button"
          onClick={() => void openPrivacyPolicy()}
          className="text-center text-xs text-muted-foreground underline underline-offset-4"
        >
          Read our privacy policy
        </button>
      </div>
    </SheetContent>
  </Sheet>
);

const AiConsentDialog: React.FC = () => {
  const { sheetOpen, decide } = useAiConsent();
  return <AiConsentSheet open={sheetOpen} onDecide={(status) => void decide(status)} />;
};

export default AiConsentDialog;
